import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./../../../assets/css/mentor/mentordashboard/Mentorhelp&support.css";
import MyMasterLayout from "../../../layouts/Mentor/MyMasterlayout";
import AppContext from "./../../../context/Appcontext";

// ── Status Badge ──
const StatusBadge = ({ status }) => {
  const map = {
    Submitted: "hs-badge-gray",
    "Under Review": "hs-badge-blue",
    "In Progress": "hs-badge-orange",
    Resolved: "hs-badge-green",
    Closed: "hs-badge-dark",
  };
  return (
    <span className={`hs-badge ${map[status] || "hs-badge-gray"}`}>
      {status}
    </span>
  );
};

// ── Priority Badge ──
const PriorityBadge = ({ priority }) => {
  const map = {
    Low: "hs-pri-low",
    Medium: "hs-pri-medium",
    High: "hs-pri-high",
  };
  return (
    <span className={`hs-priority ${map[priority] || "hs-pri-low"}`}>
      {priority}
    </span>
  );
};

// ════════════════════════════════
// VIEW 1 — LIST  (defined OUTSIDE HelpSupport)
// ════════════════════════════════
const ListView = ({
  tickets,
  isLoading,
  successMsg,
  errorMsg,
  onCreate,
  onView,
}) => (
  <div className="hs-wrapper">
    <div className="hs-page-header">
      <div className="hs-header-left">
        <div className="hs-header-icon">
          <i className="bi bi-headset"></i>
        </div>
        <div>
          <h1 className="hs-page-title">Help & Support</h1>
          <p className="hs-page-subtitle">
            Need help? Create a support ticket and our team will assist you.
          </p>
        </div>
      </div>
      <button className="hs-btn-create" onClick={onCreate}>
        <i className="bi bi-plus-lg"></i> Create Ticket
      </button>
    </div>

    {successMsg && (
      <div className="hs-success-alert">
        <i className="bi bi-check-circle-fill"></i> {successMsg}
      </div>
    )}
    {errorMsg && (
      <div className="hs-error-alert">
        <i className="bi bi-exclamation-circle-fill"></i> {errorMsg}
      </div>
    )}

    <div className="hs-stats-row">
      {[
        {
          icon: "bi-ticket-detailed-fill",
          color: "hs-icon-maroon",
          num: tickets.length,
          label: "Total Tickets",
        },
        {
          icon: "bi-hourglass-split",
          color: "hs-icon-orange",
          num: tickets.filter((t) => t.status === "In Progress").length,
          label: "In Progress",
        },
        {
          icon: "bi-eye-fill",
          color: "hs-icon-blue",
          num: tickets.filter((t) => t.status === "Under Review").length,
          label: "Under Review",
        },
        {
          icon: "bi-check-circle-fill",
          color: "hs-icon-green",
          num: tickets.filter((t) => t.status === "Resolved").length,
          label: "Resolved",
        },
      ].map((s, i) => (
        <div key={i} className="hs-stat-card">
          <div className={`hs-stat-icon ${s.color}`}>
            <i className={`bi ${s.icon}`}></i>
          </div>
          <div>
            <p className="hs-stat-num">{s.num}</p>
            <p className="hs-stat-label">{s.label}</p>
          </div>
        </div>
      ))}
    </div>

    <div className="hs-card">
      <div className="hs-card-header">
        <h2 className="hs-card-title">
          <i className="bi bi-list-ul"></i> My Tickets
        </h2>
      </div>

      {isLoading ? (
        <div className="hs-loading">
          <i className="bi bi-hourglass-split"></i> Loading tickets...
        </div>
      ) : tickets.length === 0 ? (
        <div className="hs-empty">
          <i className="bi bi-ticket-detailed hs-empty-icon"></i>
          <p className="hs-empty-title">No tickets yet</p>
          <p className="hs-empty-sub">
            Click "Create Ticket" to submit your first support request.
          </p>
        </div>
      ) : (
        <div className="hs-table-wrap">
          <table className="hs-table">
            <thead>
              <tr className="hs-thead">
                <th className="hs-th">Ticket ID</th>
                <th className="hs-th">Issue Type</th>
                <th className="hs-th">Subject</th>
                <th className="hs-th hs-th-center">Priority</th>
                <th className="hs-th hs-th-center">Status</th>
                <th className="hs-th hs-th-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t, idx) => (
                <tr
                  key={t.id}
                  className={`hs-tr ${idx % 2 === 0 ? "hs-tr-even" : "hs-tr-odd"}`}
                >
                  <td className="hs-td">
                    <span className="hs-ticket-id">#{t.id}</span>
                  </td>
                  <td className="hs-td">{t.issue_type}</td>
                  <td className="hs-td">{t.subject}</td>
                  <td className="hs-td hs-td-center">
                    <PriorityBadge priority={t.priority} />
                  </td>
                  <td className="hs-td hs-td-center">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="hs-td hs-td-center">
                    <button className="hs-btn-view" onClick={() => onView(t)}>
                      <i className="bi bi-eye-fill"></i> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

// ════════════════════════════════
// VIEW 2 — CREATE TICKET  (defined OUTSIDE HelpSupport)
// ════════════════════════════════
const CreateView = ({
  form,
  setForm,
  errorMsg,
  isFormValid,
  submitLoading,
  onCancel,
  onSubmit,
}) => (
  <div className="hs-wrapper">
    <div className="hs-page-header">
      <div className="hs-header-left">
        <button className="hs-btn-back" onClick={onCancel}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <div>
          <h1 className="hs-page-title">Create Support Ticket</h1>
          <p className="hs-page-subtitle">
            Fill in the details and our team will get back to you.
          </p>
        </div>
      </div>
    </div>

    {errorMsg && (
      <div className="hs-error-alert">
        <i className="bi bi-exclamation-circle-fill"></i> {errorMsg}
      </div>
    )}

    <div className="hs-form-card">
      <div className="hs-form-group">
        <label className="hs-label">
          Issue Type <span className="hs-required">*</span>
        </label>
        <div className="hs-radio-group">
          {["Technical Support", "User Dispute", "Payment Issue", "Other"].map(
            (type) => (
              <label
                key={type}
                className={`hs-radio-option ${form.issueType === type ? "hs-radio-active" : ""}`}
              >
                <input
                  type="radio"
                  name="issueType"
                  value={type}
                  checked={form.issueType === type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, issueType: e.target.value }))
                  }
                />
                <span>{type}</span>
              </label>
            ),
          )}
        </div>
      </div>

      <div className="hs-form-group">
        <label className="hs-label">
          Subject <span className="hs-required">*</span>
        </label>
        <input
          type="text"
          className="hs-input"
          placeholder="Enter issue title"
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
        />
      </div>

      <div className="hs-form-group">
        <label className="hs-label">
          Description <span className="hs-required">*</span>
        </label>
        <textarea
          className="hs-textarea"
          rows={5}
          placeholder="Explain your issue in detail..."
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
        />
      </div>

      <div className="hs-form-group">
        <label className="hs-label">Priority</label>
        <div className="hs-priority-group">
          {["Low", "Medium", "High"].map((p) => (
            <button
              key={p}
              type="button"
              className={`hs-priority-btn hs-pri-btn-${p.toLowerCase()} ${form.priority === p ? "hs-pri-btn-active" : ""}`}
              onClick={() => setForm((f) => ({ ...f, priority: p }))}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="hs-form-group">
        <label className="hs-label">
          Attachment <span className="hs-optional">(optional)</span>
        </label>
        <label className="hs-file-upload">
          <i className="bi bi-cloud-upload-fill"></i>
          <span>
            {form.attachment ? form.attachment.name : "Upload Screenshot"}
          </span>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) =>
              setForm((f) => ({ ...f, attachment: e.target.files[0] }))
            }
          />
        </label>
      </div>

      <div className="hs-form-footer">
        <button type="button" className="hs-btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="hs-btn-submit"
          onClick={onSubmit}
          disabled={!isFormValid || submitLoading}
          style={{
            opacity: isFormValid ? 1 : 0.5,
            cursor: isFormValid ? "pointer" : "not-allowed",
          }}
        >
          {submitLoading ? (
            <>
              <i className="bi bi-hourglass-split"></i> Submitting...
            </>
          ) : (
            <>
              <i className="bi bi-send-fill"></i> Submit Ticket
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

// ════════════════════════════════
// VIEW 3 — TICKET DETAIL  (defined OUTSIDE HelpSupport)
// ════════════════════════════════
const DetailView = ({ ticket, onBack }) => (
  <div className="hs-wrapper">
    <div className="hs-page-header">
      <div className="hs-header-left">
        <button className="hs-btn-back" onClick={onBack}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <div>
          <h1 className="hs-page-title">Ticket Detail</h1>
          <p className="hs-page-subtitle">
            View your ticket details and admin response.
          </p>
        </div>
      </div>
    </div>

    <div className="hs-detail-card">
      <div className="hs-detail-header">
        <div className="hs-detail-meta">
          <span className="hs-ticket-id-lg">#{ticket.id}</span>
          <StatusBadge status={ticket.status} />
          <PriorityBadge priority={ticket.priority} />
        </div>
        <p className="hs-detail-date">
          <i className="bi bi-calendar3"></i>{" "}
          {new Date(ticket.created_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </p>
      </div>

      <div className="hs-detail-row">
        <div className="hs-detail-item">
          <p className="hs-detail-key">Issue Type</p>
          <p className="hs-detail-val">{ticket.issue_type}</p>
        </div>
        <div className="hs-detail-item">
          <p className="hs-detail-key">Subject</p>
          <p className="hs-detail-val">{ticket.subject}</p>
        </div>
      </div>

      <div className="hs-detail-section">
        <p className="hs-section-title">
          <i className="bi bi-chat-left-text-fill"></i> Your Description
        </p>
        <div className="hs-description-box">{ticket.description}</div>
      </div>

      <div className="hs-detail-section">
        <p className="hs-section-title">
          <i className="bi bi-shield-check-fill"></i> Admin Reply
        </p>
        {ticket.admin_reply ? (
          <div className="hs-admin-reply-box">
            <div className="hs-admin-avatar">
              <i className="bi bi-person-badge-fill"></i>
            </div>
            <div className="hs-admin-reply-text">{ticket.admin_reply}</div>
          </div>
        ) : (
          <div className="hs-no-reply">
            <i className="bi bi-hourglass-split"></i>
            <p>No reply yet. Our team will respond shortly.</p>
          </div>
        )}
      </div>

      <div className="hs-detail-footer">
        <button className="hs-btn-cancel" onClick={onBack}>
          <i className="bi bi-arrow-left"></i> Back to Tickets
        </button>
      </div>
    </div>
  </div>
);

// ════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════
const HelpSupport = () => {
  const { baseUrl } = useContext(AppContext);
  const mentor_id = JSON.parse(localStorage.getItem("mentorData"))?.id;

  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    issueType: "",
    subject: "",
    description: "",
    priority: "Medium",
    attachment: null,
  });

  const isFormValid =
    form.issueType && form.subject.trim() && form.description.trim();

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/General/help-tickets/${mentor_id}`,
      );
      if (response.data.success) setTickets(response.data.tickets);
    } catch {
      setErrorMsg("Failed to load tickets. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (mentor_id) fetchTickets();
  }, [mentor_id]);

  const handleViewTicket = async (ticket) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/General/help-tickets/detail/${ticket.id}`,
      );
      if (response.data.success) {
        setSelectedTicket(response.data.ticket);
        setView("detail");
      }
    } catch {
      setErrorMsg("Failed to load ticket details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setSubmitLoading(true);
    setErrorMsg("");
    try {
      const formData = new FormData();
      formData.append("mentor_id", mentor_id);
      formData.append("issue_type", form.issueType);
      formData.append("subject", form.subject);
      formData.append("description", form.description);
      formData.append("priority", form.priority);
      if (form.attachment) formData.append("attachment", form.attachment);

      const response = await axios.post(
        `${baseUrl}/General/help-tickets/create`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.data.success) {
        setForm({
          issueType: "",
          subject: "",
          description: "",
          priority: "Medium",
          attachment: null,
        });
        setSuccessMsg("Ticket submitted successfully!");
        setView("list");
        fetchTickets();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch {
      setErrorMsg("Failed to submit ticket. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MyMasterLayout>
      {view === "list" && (
        <ListView
          tickets={tickets}
          isLoading={isLoading}
          successMsg={successMsg}
          errorMsg={errorMsg}
          onCreate={() => setView("create")}
          onView={handleViewTicket}
        />
      )}
      {view === "create" && (
        <CreateView
          form={form}
          setForm={setForm}
          errorMsg={errorMsg}
          isFormValid={isFormValid}
          submitLoading={submitLoading}
          onCancel={() => setView("list")}
          onSubmit={handleSubmit}
        />
      )}
      {view === "detail" && selectedTicket && (
        <DetailView ticket={selectedTicket} onBack={() => setView("list")} />
      )}
    </MyMasterLayout>
  );
};

export default HelpSupport;
